import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, Lock, LogOut, Sparkles } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

export default function TrialGuard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!token || user?.is_admin) return;
    axios.get(`${API}/payments/subscription`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSub(res.data))
      .catch(() => {});
  }, [token, user]);

  if (!sub || user?.is_admin) return null;

  const end = sub.trial_ends_at || sub.trial_end;
  const endDate = end ? new Date(end) : null;
  const now = new Date();
  const daysLeft = endDate ? Math.ceil((endDate - now) / 86400000) : null;
  const isTrial = sub.status === 'trial';
  const expired = sub.status === 'trial_expired' || (isTrial && endDate && endDate <= now);

  const goToPricing = () => { window.location.href = '/#pricing'; };

  if (expired) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#0a1628]/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="trial-expired-overlay">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-900 to-[#0d1f35] border border-white/10 rounded-2xl p-8 text-center space-y-5">
          <div className="w-14 h-14 mx-auto rounded-full bg-purple-500/15 flex items-center justify-center">
            <Lock className="w-7 h-7 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Your free trial has ended</h2>
          <p className="text-gray-400 text-sm leading-relaxed">
            Mia has paused answering your calls and chats. Subscribe to reactivate everything exactly as you left it — your data, CRM and call history are safe.
          </p>
          {sub.status === 'trial_expired' && !sub.numbers_released && (
            <p className="text-amber-400/90 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              Your AI phone number is reserved for a limited time — subscribe soon to keep it.
            </p>
          )}
          <button
            onClick={goToPricing}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-sm transition-all"
            data-testid="trial-expired-subscribe-btn"
          >
            <Sparkles className="w-4 h-4 inline mr-2" />Choose a Plan — from $45/mo
          </button>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="text-gray-500 hover:text-gray-300 text-xs flex items-center gap-1.5 mx-auto transition-colors"
            data-testid="trial-expired-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
      </div>
    );
  }

  if (isTrial && daysLeft !== null && daysLeft <= 7) {
    return (
      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-purple-500/30 px-4 py-2 flex items-center justify-center gap-3 flex-wrap" data-testid="trial-banner">
        <span className="flex items-center gap-2 text-sm text-purple-200">
          <Clock className="w-4 h-4 text-purple-300" />
          <span data-testid="trial-days-left">
            {daysLeft <= 0 ? 'Your trial ends today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial`}
          </span>
        </span>
        <button
          onClick={goToPricing}
          className="text-xs font-semibold text-white bg-purple-600 hover:bg-purple-500 px-3 py-1 rounded-full transition-colors"
          data-testid="trial-banner-choose-plan-btn"
        >
          Choose a plan
        </button>
      </div>
    );
  }

  return null;
}
